/**
 * Vim-style list navigation hook for staff lists.
 *
 * Per Decisions §19 + INTERACTION_SPEC §10:
 *   - `j` / down  → next item
 *   - `k` / up    → previous item
 *   - `Enter`     → open focused item (caller-supplied handler)
 *
 * Caller supplies the ordered list of ids and an `onOpen` callback.
 * The hook tracks the focused index in component state and exposes
 * the current id + a setter so the row can render a focus indicator
 * (and so click-to-peek can sync the cursor).
 *
 * The hotkeys are skipped automatically when an input/textarea has
 * focus (see useHotkeys), so typing "j" in the search box won't jump
 * tickets.
 */

import { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from './use-hotkeys.js';

export interface UseListNavReturn {
  focusedIndex: number;
  focusedId: string | null;
  /** Manually move focus to a specific id (used when click-to-peek
   *  syncs the keyboard cursor with the mouse cursor). */
  setFocusedId: (id: string | null) => void;
}

interface UseListNavOptions {
  /** Ordered ids of currently-visible rows. Reset focus to 0 if the
   *  current focus falls off the end (e.g. after filter change). */
  ids: string[];
  /** Called when Enter is pressed with a focused row. */
  onOpen: (id: string) => void;
  /** When true the hotkeys are inert. Useful while a modal is open. */
  disabled?: boolean;
}

export function useListNav({
  ids,
  onOpen,
  disabled,
}: UseListNavOptions): UseListNavReturn {
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Clamp the focus when the underlying list shrinks (e.g. after a
  // filter change). Keep at 0 when empty so we don't track -1.
  useEffect(() => {
    if (focusedIndex >= ids.length) {
      setFocusedIndex(Math.max(0, ids.length - 1));
    }
  }, [ids.length, focusedIndex]);

  const setFocusedId = useCallback(
    (id: string | null) => {
      if (id === null) return;
      const idx = ids.indexOf(id);
      if (idx >= 0) setFocusedIndex(idx);
    },
    [ids]
  );

  useHotkeys(
    {
      j: () => setFocusedIndex((i) => Math.min(ids.length - 1, i + 1)),
      down: () => setFocusedIndex((i) => Math.min(ids.length - 1, i + 1)),
      k: () => setFocusedIndex((i) => Math.max(0, i - 1)),
      up: () => setFocusedIndex((i) => Math.max(0, i - 1)),
      enter: () => {
        const id = ids[focusedIndex];
        if (id) onOpen(id);
      },
    },
    { disabled }
  );

  const focusedId = ids[focusedIndex] ?? null;

  return { focusedIndex, focusedId, setFocusedId };
}
